import EditableItem from '#/components/cms/EditableItem'
import type { EditField } from '#/components/cms/EditModal'
import Container from '#/components/layout/Container'
import Section from '#/components/layout/Section'
import { Badge } from '#/components/ui/badge'
import { OptimizedImage } from '#/components/ui/optimized-image'
import { getBlogPosts } from '#/lib/content'
import { useCmsContent } from '#/hooks/useCmsContent'
import { createFileRoute, Link, useParams } from '@tanstack/react-router'
import { lazy, Suspense } from 'react'
import { ArrowLeft } from 'lucide-react'
import { seoMeta, canonicalLink, SITE_URL } from '#/lib/seo'

const MDEditorMarkdown = lazy(() =>
  import('@uiw/react-md-editor').then((mod) => ({ default: mod.default.Markdown }))
)

const postFields: EditField[] = [
  { name: 'title', type: 'text', label: 'Title' },
  { name: 'excerpt', type: 'textarea', label: 'Excerpt' },
  { name: 'author', type: 'text', label: 'Author' },
  { name: 'publishedAt', type: 'text', label: 'Published Date (YYYY-MM-DD)' },
  { name: 'tags', type: 'tags', label: 'Tags' },
  { name: 'image', type: 'image', label: 'Image' },
  { name: 'content', type: 'markdown', label: 'Content' },
]

export const Route = createFileRoute('/blog/$slug')({
  head: ({ match }) => {
    const post = match.loaderData?.posts?.find(
      (p: { slug: string }) => p.slug === match.params.slug,
    )
    if (!post) return {}
    const imageUrl = post.image.startsWith('http')
      ? post.image
      : `${SITE_URL}${post.image}`
    return {
      meta: seoMeta({
        title: post.title,
        description: post.excerpt,
        path: `/blog/${post.slug}`,
        image: post.image,
        type: 'article',
        article: {
          publishedTime: post.publishedAt,
          author: post.author,
          tags: post.tags,
        },
      }),
      links: [canonicalLink(`/blog/${post.slug}`)],
      scripts: [
        {
          type: 'application/ld+json',
          children: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BlogPosting',
            headline: post.title,
            description: post.excerpt,
            image: imageUrl,
            author: { '@type': 'Person', name: post.author },
            datePublished: post.publishedAt,
            publisher: {
              '@type': 'Organization',
              name: 'Main Street Bread',
              url: SITE_URL,
            },
            mainEntityOfPage: `${SITE_URL}/blog/${post.slug}`,
          }),
        },
      ],
    }
  },
  loader: async () => {
    const posts = await getBlogPosts()
    return { posts }
  },
  component: BlogPostPage,
})

function BlogPostPage() {
  const { posts: deployedPosts } = Route.useLoaderData()
  const { slug } = useParams({ from: '/blog/$slug' })
  const allPosts = useCmsContent('blog-posts', deployedPosts)
  const post = allPosts.find((p) => p.slug === slug)

  if (!post) {
    return (
      <main>
        <Section tone="surface">
          <Container className="max-w-3xl py-16 text-center">
            <h1 className="font-serif text-3xl font-bold text-on-surface">Post not found</h1>
            <Link to="/blog" className="mt-4 inline-block text-primary hover:underline">
              Back to blog
            </Link>
          </Container>
        </Section>
      </main>
    )
  }

  const formattedDate = new Date(post.publishedAt).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  })

  return (
    <main>
      <Section tone="surface">
        <Container className="max-w-3xl">
          {/* Back link */}
          <Link
            to="/blog"
            className="mb-8 inline-flex items-center gap-1 text-sm text-on-surface-variant no-underline transition-colors hover:text-primary"
          >
            <ArrowLeft className="size-4" />
            Back to Blog
          </Link>

          <EditableItem
            file="blog-posts"
            itemId={post.id}
            fields={postFields}
            values={{ title: post.title, excerpt: post.excerpt, author: post.author, publishedAt: post.publishedAt, tags: post.tags, image: post.image, content: post.content }}
            title={`Edit: ${post.title}`}
            deletable
          >
            {/* Hero image */}
            <div
              className="mb-8 aspect-[2/1] w-full overflow-hidden rounded-lg bg-gradient-to-br from-primary-container/15 to-surface-high"
              style={{ boxShadow: 'var(--shadow-elevated)' }}
            >
              <OptimizedImage
                src={post.image}
                lqip={post.imageLqip}
                alt={post.title}
                sizes="(min-width: 1024px) 800px, 100vw"
                className="h-full w-full"
                loading="eager"
                fetchPriority="high"
              />
            </div>

            {/* Meta */}
            <div className="mb-4 flex flex-wrap items-center gap-2">
              {post.tags.map((tag) => (
                <Badge key={tag} variant="secondary" className="text-xs">
                  {tag}
                </Badge>
              ))}
            </div>

            {/* Title */}
            <h1 className="mb-4 font-serif text-3xl font-bold leading-tight text-on-surface sm:text-4xl">
              {post.title}
            </h1>

            <div className="mb-6 flex items-center gap-3 text-sm text-on-surface-variant">
              {/* Author avatar placeholder */}
              <div className="flex size-8 items-center justify-center rounded-full bg-gradient-to-br from-primary-container/30 to-tertiary-container/20 text-xs font-bold text-primary-container">
                {post.author.split(' ').map(n => n[0]).join('')}
              </div>
              <span className="font-medium">{post.author}</span>
              <span>&middot;</span>
              <span>{formattedDate}</span>
            </div>

            {/* Decorative divider */}
            <div className="mb-8 h-px w-full bg-gradient-to-r from-primary-container/30 via-tertiary-container/20 to-transparent" />

            {/* Content */}
            <article className="prose prose-lg max-w-none" data-color-mode="light">
              <Suspense fallback={<div />}>
                <MDEditorMarkdown
                  source={post.content}
                  style={{ background: 'transparent', color: 'inherit', fontFamily: 'inherit' }}
                />
              </Suspense>
            </article>
          </EditableItem>
        </Container>
      </Section>
    </main>
  )
}
